import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { MyTaskDto } from '@taqon/contracts';
import { Link as RouterLink } from 'react-router';
import { formatEstimate, PRIORITY_COLOURS, PRIORITY_LABELS } from './api';
import { groupOf, groupTasks, remaining, taskHref } from './my-tasks';

/**
 * The caller's open tasks, grouped by when they are due (CLR-66), each with the time remaining,
 * its project, status, priority and estimate, and a link to the task. `limit` keeps only the first
 * tasks of each group, for the dashboard.
 */
export function MyTaskList({
  tasks,
  today,
  limit,
  onOpen,
}: {
  tasks: readonly MyTaskDto[];
  today: string;
  limit?: number;
  /** Called as a task link is followed, so a popup can close. */
  onOpen?: () => void;
}) {
  const groups = groupTasks(tasks, today);
  return (
    <Stack spacing={2}>
      {groups.map((group) => {
        const shown = limit === undefined ? group.tasks : group.tasks.slice(0, limit);
        const more = group.tasks.length - shown.length;
        return (
          <Box key={group.key} component="section" aria-label={group.title}>
            <Typography
              variant="subtitle2"
              component="h3"
              sx={{ color: group.key === 'overdue' ? 'error.main' : 'text.secondary', mb: 0.5 }}
            >
              {group.title} ({group.tasks.length})
            </Typography>
            <Stack component="ul" spacing={0.75} sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {shown.map((task) => (
                <MyTaskRow key={task.id} task={task} today={today} onOpen={onOpen} />
              ))}
            </Stack>
            {more > 0 && (
              <Typography variant="caption" color="text.secondary">
                and {more} more
              </Typography>
            )}
          </Box>
        );
      })}
    </Stack>
  );
}

function MyTaskRow({
  task,
  today,
  onOpen,
}: {
  task: MyTaskDto;
  today: string;
  onOpen: (() => void) | undefined;
}) {
  const left = remaining(task.dueDate, today);
  const overdue = groupOf(task.dueDate, today) === 'overdue';
  const estimate = formatEstimate(task.estimateMinutes);
  return (
    <Box
      component="li"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1,
        py: 0.75,
        px: 1.25,
        borderRadius: 1.5,
        border: 1,
        borderColor: overdue ? 'error.light' : 'divider',
      }}
    >
      <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
        <Link
          component={RouterLink}
          to={taskHref(task)}
          onClick={onOpen}
          sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}
        >
          {task.key} · {task.title}
        </Link>
        <Typography variant="caption" color="text.secondary" component="div">
          {task.projectName} · {task.columnName}
          {estimate === null ? '' : ` · estimate ${estimate}`}
          {task.checklist.total > 0
            ? ` · checklist ${String(task.checklist.done)}/${String(task.checklist.total)}`
            : ''}
        </Typography>
      </Box>
      <Chip
        size="small"
        variant="outlined"
        label={PRIORITY_LABELS[task.priority]}
        color={PRIORITY_COLOURS[task.priority]}
      />
      {left !== null && (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: overdue ? 'error.main' : 'text.primary',
            whiteSpace: 'nowrap',
          }}
        >
          {left}
        </Typography>
      )}
    </Box>
  );
}
